import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Box from "@amzn/meridian/esm/components/box";
import SummaryCard from "../../common/summary/SummaryCard";
import { Pagination, Theme } from "@amzn/meridian";
import Loading from "../../common/Loading";
import NoSummary from "../../inMeeting/././summary/NoSummary";
import { fetchSummaries, generateSummaryGroup, preProcessInClient } from "../../utils/SummaryUtil";
import { sampleSummaryList } from "../../../localtest/LocalhostTest";
import LogManager from "../../utils/LogManager";
import CommonErrorModal from "../../common/error/CommonErrorModal";

/**
 * SummariesTabInClient renders a paginated view of meeting summaries in the Zoom client.
 *
 * Behavior:
 * - Fetches summaries from the backend using `fetchSummaries`.
 * - Summaries are grouped by date (e.g., "Today", "Yesterday") using `generateSummaryGroup` or `preProcessInMeeting`.
 * - Each group is paginated into chunks of 4 summaries via `preProcessInClient`.
 * - Displays loading, error, or empty states as appropriate.
 *
 * UI:
 * - Displays a header box with the current group title.
 * - Renders 2x2 summary cards per page.
 * - Provides pagination controls to navigate between pages.
 *
 * Used in: AIMS Zoom app when user clicks the "Summaries" tab from the client
 */
const SummariesTabInClient = (props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [summaryGroups, setSummaryGroups] = useState({});
  const [isSummaryEmpty, setIsSummaryEmpty] = useState(false);
  const [showServerError, setShowServerError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);

  const onViewMoreClick = (summaryData) => {
    console.log("Navigating to detail with fromPage:", currentPage);

    navigate(`/client/summary/${summaryData.aimsUuid}`, {
      state: {
        summary: summaryData,
        fromPage: currentPage,
      },
    });
  };

  useEffect(() => {
    const returnPage = props.returnPage || location.state?.returnPage;
    // Try reading from sessionStorage
    const cached = sessionStorage.getItem("summaryCache");
    if (cached) {
      const groups = JSON.parse(cached);
      const processedPages = preProcessInClient(groups);
      setSummaryGroups(groups);
      setPages(processedPages);
      setIsLoading(false);

      if (returnPage && returnPage >= 1 && returnPage <= processedPages.length) {
        setCurrentPage(returnPage);
      } else {
        setCurrentPage(1); // reset to first page on full refresh or no returnPage
      }
    }

    // Otherwise: load from backend
    if (window.location.href.includes("http://localhost:3000/")) {
      console.log("[Localhost] Running SummariesTabInClient in localhost. Using sampleSummaryList.");
      setIsSummaryEmpty(false);

      const groups = generateSummaryGroup(sampleSummaryList);
      sessionStorage.setItem("summaryCache", JSON.stringify(groups));
      setSummaryGroups(groups);
      setPages(preProcessInClient(groups));
      setIsLoading(false);
      setShowServerError(false);
    } else {
      fetchSummaries()
        .then((summaryListJson) => {
          if (summaryListJson.length === 0) {
            setIsSummaryEmpty(true);
            setIsLoading(false);
          } else {
            const groups = generateSummaryGroup(summaryListJson);
            console.log("group is", groups);
            sessionStorage.setItem("summaryCache", JSON.stringify(groups));
            setSummaryGroups(groups);
            setPages(preProcessInClient(groups));
            setIsLoading(false);
          }
        })
        .catch((error) => {
          LogManager.error("Failed to fetch summaries:", error);
          setShowServerError(true);
          setIsLoading(false);
        });
    }
  }, [location.state]);

  if (isLoading) return <Loading />;
  if (showServerError) {
    return <CommonErrorModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />;
  }

  if (isSummaryEmpty || pages.length === 0) {
    return (
      <Column spacing="none" spacingInset="none" alignment="top" alignmentHorizontal="start">
        <Column height="96px" />

        <Row spacing="none" spacingInset="none">
          <Row width="184px" />
          <NoSummary />
        </Row>
      </Column>
    );
  }

  const { groupTitle, summaries } = pages[currentPage - 1];

  return (
    <Column spacing="none" spacingInset="none" alignmentVertical="top" alignmentHorizontal="start">
      <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
        {/*Date box*/}

        <Box type="outline" spacing="none" spacingInset="none" minHeight={68} minWidth={306}>
          <Column spacing="none" spacingInset="none">
            <Column height="32px" />
            <Row>
              <Row width={32} />
              <Text fontFamily="amazonEmber" type="h100">
                {groupTitle}
              </Text>
            </Row>
          </Column>
        </Box>

        {/* Summary Cards */}
        <Box type="outline" spacing="none" spacingInset="none" minHeight={356} width="660px">
          <Row spacing="none" spacingInset="none">
            <Row width="16px" />
            <Column spacing="none" spacingInset="none">
              {[0, 1].map((rowIndex) => (
                <Row key={rowIndex} spacing="none" spacingInset="none">
                  {[0, 1].map((colIndex) => {
                    const index = rowIndex * 2 + colIndex;
                    const summary = summaries[index];
                    if (!summary) return null;
                    return (
                      <Box key={summary.aimsUuid} spacingInset="300">
                        <SummaryCard summary={summary} onViewMoreClick={onViewMoreClick} />
                      </Box>
                    );
                  })}
                </Row>
              ))}
            </Column>
          </Row>
        </Box>

        <Column height="16px" />

        {/*Pagination Box*/}
        <Box type="outline" spacing="none" spacingInset="none" height="40px" width="660px">
          <Row spacing="none" spacingInset="none">
            <Row width="396px" />
            <Pagination numberOfPages={pages.length} onChange={setCurrentPage} currentPage={currentPage} showSkipArrows={true} disabled={false} />
          </Row>
        </Box>
      </Theme>
    </Column>
  );
};

export default SummariesTabInClient;
